import {
  FULL_FIDELITY_MODES,
  type FullFidelityMode,
} from '../../components/WorkspaceDrawer/Settings/types';
import {type BooleanDynamicConfig} from '../useDynamicConfig';
import {useLinePlotDynamicConfigStore} from './stores/LinePlotDynamicConfigStore';

/**
 * Public readers for line-plot Statsig dynamic configs. The real evaluation
 * lives in `./stores/LinePlotDynamicConfigStore` (hydrated once per route via
 * `useInitializeLinePlotDynamicConfigStore`); these hooks are thin accessors
 * over that precomputed snapshot so panels don't re-run the cross-org
 * ranking/merging on every render.
 */

export {
  PRIMARY_LINE_TYPES,
  type PrimaryLineType,
} from './stores/LinePlotDynamicConfigStore';

export const usePrimaryLineType = () => {
  return useLinePlotDynamicConfigStore(state => state.primaryLineType);
};

/**
 * Returns whether min/max/last bin visualization mode is enabled via the
 * `enableMinMaxLastMode` flag on the route-hydrated line plot dynamic-config
 * snapshot.
 */
export const useEnableMinMaxLastMode = (): BooleanDynamicConfig => {
  const value = useLinePlotDynamicConfigStore(
    state => state.isMinMaxLastModeEnabled
  );
  return {value, loading: false, error: undefined};
};

/**
 * Derives a `PrimaryLineType` from a `fullFidelityMode` setting value, gated
 * on `enableMinMaxLastMode`. Unlike `usePrimaryLineType` (which aggregates an
 * org-level Statsig config across the viewer's organizations), this hook's
 * input is the `fullFidelityMode` the caller already holds in hand - typically
 * the cascaded panel/workspace setting. Only two values are produced:
 * `'min-max-last'` when the gate is open AND `fullFidelityMode` is explicitly
 * `'min-max-last'`, and `'full-fidelity'` otherwise. The `'lttb'` branch is
 * intentionally out of scope at this call site.
 */
export const useFullFidelityModeLineType = (
  fullFidelityMode: FullFidelityMode | undefined
) => {
  const {value: enableMinMaxLastMode} = useEnableMinMaxLastMode();
  if (
    enableMinMaxLastMode &&
    fullFidelityMode === FULL_FIDELITY_MODES.MinMaxLast
  ) {
    return 'min-max-last';
  }
  return 'full-fidelity';
};

export const useLinePlotMaxRunsDynamicConfig = () => {
  return useLinePlotDynamicConfigStore(state => state.linePlotMaxRuns);
};

export const isValidGroupingDepth = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value);
};

export const useFullScreenLinePlotMaxRunsDynamicConfig = () => {
  return useLinePlotDynamicConfigStore(
    state => state.fullScreenLinePlotMaxRuns
  );
};

export const useLinePlotGroupingDepthDynamicConfig = () => {
  return useLinePlotDynamicConfigStore(state => state.groupingDepth);
};
